// Amenity helpers — single source of truth for the data shape and
// presentation strings used when synthesizing vendor records from
// amenity-typed Position rows. Marker rendering, label generation, and
// category synthesis all read from here so amenities stay coherent.

import type { Category, Position, Vendor } from '../types';

export type AmenityType = 'atm' | 'first_aid' | 'restroom' | 'stage';

export const AMENITY_CATEGORY_ID = 'amenities';
export const ENTERTAINMENT_CATEGORY_ID = 'entertainment';

export const AMENITY_LABELS: Record<AmenityType, string> = {
  atm: 'ATM',
  first_aid: 'First Aid',
  restroom: 'Restroom',
  stage: 'Music Stage',
};

export const AMENITY_DESCRIPTIONS: Record<AmenityType, string> = {
  atm: 'Cash machine. Available during market hours',
  first_aid: 'Staffed first-aid station. Available during market hours',
  restroom: 'Indoor restroom facility. Available during market hours',
  stage: 'Catch live music from local musicians every Saturday',
};

/** Search-only synonyms carried on the synthesized amenity Vendor as
 *  `keywords` so colloquial queries ("bathroom", "cash machine") match
 *  even though they appear nowhere in the rendered name/description.
 *  Keep entries lowercase — the search predicate compares lowercased. */
export const AMENITY_KEYWORDS: Record<AmenityType, string[]> = {
  atm: ['cash', 'money', 'cash machine'],
  first_aid: ['medic', 'medical', 'nurse', 'aid'],
  restroom: ['bathroom', 'toilet', 'wc', 'washroom'],
  stage: ['music', 'band', 'concert', 'live music'],
};

export function isAmenityType(t: string): t is AmenityType {
  return t === 'atm' || t === 'first_aid' || t === 'restroom' || t === 'stage';
}

/** The category IDs that should render with the brick-and-mortar treatment
 *  (slate fill, square shape, brownstone glyph) — distinct from the
 *  farmer-style circular VENDOR_GLYPH that all other vendor categories
 *  share. `store` is the legacy alias kept here so older Sheets that
 *  haven't migrated still resolve correctly.
 *
 *  Exported so `useCategoryStyleSheet` can derive the matching CSS
 *  selectors from this single source of truth — keep new aliases here
 *  and the stylesheet picks them up automatically. */
export const STOREFRONT_CATEGORY_IDS: ReadonlySet<string> = new Set(['storefront', 'store']);

export function isStorefrontCategoryId(id: string): boolean {
  return STOREFRONT_CATEGORY_IDS.has(id.toLowerCase());
}

/** Fill color used by storefront-category vendors — same slate token
 *  the restroom amenity uses, so the brick-and-mortar marker reads
 *  visually as "structure" rather than competing with the vendor palette. */
export function storefrontFill(): string {
  if (typeof document === 'undefined') return '#76849A';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--palette-slate').trim();
  return v || '#76849A';
}

/**
 * Per-type fill color for amenity surfaces (list-row avatar, detail
 * sheet header, etc.). Mirrors the marker SVG fills produced by
 * `buildMarkerSvg` so the row visual matches the map marker. Reads CSS
 * variables at runtime with hardcoded fallbacks for SSR / pre-stylesheet.
 */
const AMENITY_FILLS: Record<AmenityType, { var: string; fallback: string }> = {
  atm: { var: '--palette-amenity-money', fallback: '#2d6a4f' },
  first_aid: { var: '--palette-category-red', fallback: '#b04838' },
  restroom: { var: '--palette-slate', fallback: '#76849A' },
  stage: { var: '--palette-amenity-stage', fallback: '#906030' },
};
export function amenityFill(type: AmenityType): string {
  if (typeof document === 'undefined') return AMENITY_FILLS[type].fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(AMENITY_FILLS[type].var)
    .trim();
  return v || AMENITY_FILLS[type].fallback;
}

/** The synthetic category appended to the categories list so amenity
 *  vendors have something to bind to in the filter UI. The marker fill
 *  is overridden inside `buildMarkerSvg` per amenity type — this color
 *  drives the filter chip swatch and the row category dot only.
 *  Neutral slate so the swatch doesn't compete with the vendor category palette. */
export const AMENITY_CATEGORY: Category = {
  id: AMENITY_CATEGORY_ID,
  name: 'Amenities',
  color: '#76849A',
};

/** Synthetic category for music-stage positions. Rendered as its own
 *  "Entertainment" filter chip, separate from the "Amenities" chip. */
export const ENTERTAINMENT_CATEGORY: Category = {
  id: ENTERTAINMENT_CATEGORY_ID,
  name: 'Entertainment',
  color: '#906030',
};

/** Returns the synthetic Category that a given amenity type belongs to.
 *  Stage markers go under Entertainment; all other amenities use Amenities. */
export function syntheticCategoryFor(type: AmenityType): Category {
  return type === 'stage' ? ENTERTAINMENT_CATEGORY : AMENITY_CATEGORY;
}

/**
 * Derives a stable, URL-friendly UID for an amenity Position from its
 * `positionId` (e.g. `P:08` → `p-08`, `ATM01` → `atm01`). Unlike
 * the old `amenity_<random-uuid>` scheme, this survives data refreshes
 * so shared amenity links stay valid.
 *
 * Assumes amenity positionIds are globally unique — two amenity rows
 * sharing the same positionId would collide on UID. Vendor positions
 * can share a positionId (multi-position vendor pattern) but amenity
 * positions must not.
 */
export function amenityUidFor(positionId: string): string {
  return positionId.toLowerCase().replace(/:/g, '-');
}

/** Build a synthetic Vendor record for an amenity-typed Position so the
 *  marker, list view, search, and detail-sheet flows all work without
 *  staff having to author a Vendors-tab row. The synthetic Vendor's
 *  `name` and `description` prefer per-instance overrides on the Position
 *  row (e.g. "Arts Center Public Restrooms") and fall back to the
 *  type-based defaults when blank. */
export function synthesizeAmenityVendor(pos: Position): Vendor | null {
  if (!isAmenityType(pos.type)) return null;
  const categoryId = syntheticCategoryFor(pos.type).id;
  return {
    uid: amenityUidFor(pos.positionId),
    name: pos.name ?? AMENITY_LABELS[pos.type],
    positionId: pos.positionId,
    positionUid: pos.uid,
    categoryId,
    description: pos.description ?? AMENITY_DESCRIPTIONS[pos.type],
    keywords: AMENITY_KEYWORDS[pos.type],
    status: 'active',
    coordinates: pos.coordinates,
    type: pos.type,
    labelPosition: pos.labelPosition,
  };
}
